import { Component, Input, OnDestroy, OnInit, signal } from '@angular/core';
import { lastValueFrom, Subscription } from 'rxjs';
import { PeriforOnChangeMessages, SignalRService } from 'src/app/shared/services/signal-r.service';
import { PackerService } from 'src/app/wellbore-inputs/services/packers.service';
import { StressResultsService } from '../../services/stress-results.service';
import { UserUnitsModel } from 'src/app/core/components/user-units/user-units.model';
import { StorageKeys, StoreService } from 'src/app/core/services/store.service';
import { Store } from '@ngneat/elf';
import { PackerEnvelopeUi } from 'src/app/wellbore-inputs/models/packers.model';
import { unitsLib } from 'src/app/core/services/unit-library';
import { withUIEntities } from '@ngneat/elf-entities';
import { XyLinePlotUi } from 'src/app/core/models/xy-line-plot.model';
import { PackerLoadResultsModel } from '../../models/packer-load-results.model';

@Component({
    selector: 'app-packer-envelope-results',
    template: `
    <app-tool-loader [isLoading]="isLoading()" positionUnset="true">
      <p-toolbar>
        <ng-template #start>
          <p-select
              id="thermalOperation"
              appendTo="body"
              [options]="packers"
              [ngModel]="selectedPacker"
              appendTo="body"
              (onChange)="selectPacker($event.value)"
          ></p-select>
        </ng-template>
      </p-toolbar>

      <app-xy-line-plot
          [plotData]="plot.data"
          [xAxisTitle]="xAxisTitle"
          [yAxisTitle]="yAxisTitle"
          [plotName]="plotName"
          [downloadPlotName]="downloadPlotName"
          [plotTitle]="'Packer Envelope'"
          [reverseAutoRange]="true"
          [xyLinePlotStore]="packerEnvelopeStore"
      ></app-xy-line-plot>
      
      @if (!isLoading()) {
        <p-dialog [draggable]="false" [visible]="isDialogVisible" [closable]="false" appendTo="self">
          @if (needToCalculate) {
            <i class="pi pi-exclamation-circle pr-1"></i> <label>Please calculate PERIVIS to see results.</label>
          }
          @if (packersMissing) {
            <i class="pi pi-exclamation-circle pr-1"></i> <label>No Packers set on selected string.</label>
          }
          @if (envelopeMissing && packerLoadResults[0]?.errors != null) {
            <i class="pi pi-exclamation-circle pr-1"></i> <label>Packer Operating Envelope not specified for the selected packer.</label>
          }
          <br>
          <br>
        </p-dialog>
      }

      @if (packerLoadResults != null) {
        <app-perivis-validation [errors]="packerLoadResults[0]?.errors" />
      }
  </app-tool-loader>
  `,
    styles: [],
    standalone: false
})
export class PackerEnvelopeResultsComponent implements OnInit, OnDestroy {

  private _packerResults: any;
  private _subscriptions: Subscription = new Subscription();
  
  public packerLoadResults: PackerLoadResultsModel[];
  public packers: any[] = [];
  public tableName: string;
  public columnDefs :{ header: string, field: string }[];
  public results: any;
  public needToCalculate: boolean;
  public isLoading = signal<boolean>(false);
  public packersMissing: boolean;
  public envelopeMissing: boolean;
  public userUnits: UserUnitsModel;
  public isDarkMode: boolean;

  public plot = {
    data: [],
    layout: {
      xaxis: {},
      yaxis: {}
    },
    config: {
      scrollZoom: false,
    }
  };

  public xAxisTitle = '';
  public yAxisTitle = '';
  public plotName: string;
  public downloadPlotName = 'packer_operating_envelope';

    //State Management
    private _componentId: string;
    @Input() set componentId(value: string) {
      this._componentId = value;
      this.packerEnvelopeStore = this._storeService.createStore(this.componentId, new PackerEnvelopeUi, withUIEntities<XyLinePlotUi>());
      this.selectedPacker = this.packerEnvelopeStore.state.selectedPacker || null;
    }
    get componentId(): string {
      return this._componentId;
    }
    public packerEnvelopeStore: Store;

  public selectedPacker: string;

  constructor(
    private _packerService: PackerService,
    private _signalRService: SignalRService,
    private _stressResultsService: StressResultsService,
    private _storeService: StoreService
  ) { }

  async ngOnInit(): Promise<void> {
    this.userUnits = await this._storeService.get<UserUnitsModel>(StorageKeys.UNITS);

    this.yAxisTitle = `Axial Force: -Down [Compression] / +Up [Tension] (${ this.userUnits.force })`;
    this.xAxisTitle = `Differential Pressure: -Down [Above] / +Up [Below] (${unitsLib[this.userUnits.pressure].symbol})`;;

    this.refreshResults();

    const hub = this._signalRService.getConnectionToNotificationHub();
    this._signalRService.subscribeToEventFilteredByDesignId(hub, SignalRService.ON_PFB_CHANGE, d => this.signalRfunc(d));

    this.plotName = 'packerOperatingEnvelope - ' + this.packerEnvelopeStore.state.selectedPacker;

    this._subscriptions.add(this._storeService.getStore(StorageKeys.DARK_MODE)
        .subscribe(t => this.isDarkMode = t.isDarkMode ?? true));
  }

  signalRfunc(data: any) {
    if (data.action == PeriforOnChangeMessages.REFRESH_RESULTS || data.action == PeriforOnChangeMessages.REFRESH_PACKERS ||
      data.action == PeriforOnChangeMessages.REFRESH_STRING_INPUTS) {
      this.refreshResults();
      this.packerEnvelopeStore.reset();
    }
  }

  private async refreshResults() {
    this.isLoading.set(true);
    this._packerResults = await lastValueFrom(this._packerService.getPackersForTubular());
    this.packerLoadResults = await lastValueFrom(this._stressResultsService.getPackerLoads());

    if (this._packerResults?.packers.length == 0) {
      this.packersMissing = true;
      this.envelopeMissing = false;
      this.needToCalculate = false;
      this.packers = [];
      this.plot.data = [];
      this.isLoading.set(false);
      return;
    } else if ((this.packerLoadResults?.length ?? 0) == 0) {
      this.plot.data = [];
      this.packersMissing = false;
      this.needToCalculate = true;
      this.envelopeMissing = false;
      this.isLoading.set(false);
      return;
    } else {
      this.packersMissing = false;
      this.needToCalculate = false;
    }
    this.plot.config.scrollZoom = this.plot ? true : false;
    this.packers = this.packerLoadResults.map(x => x.packerName);
    if (!this.packerEnvelopeStore.state.selectedPacker) {
      this.packerEnvelopeStore.update(state => ({...state, selectedPacker: this.packerLoadResults[0].packerName}));
      this.selectedPacker = this.packerLoadResults[0].packerName;
    }
    this.selectPacker();
  }

  public selectPacker(packer?: string) {
    if (packer) {
      this.packerEnvelopeStore.update(state => ({...state, selectedPacker: packer}));
      this.selectedPacker = packer;
    }

    this.plotName = 'packerOperatingEnvelope - ' + this.selectedPacker;

    const selectedPackerEnvelope = this._packerResults.packers.find(x => x.name === this.selectedPacker)?.packerEnvelope;

    const index = this.packerLoadResults.findIndex(x => x.packerName === this.selectedPacker);

    const packerLoads = this.packerLoadResults ? this.packerLoadResults[index]?.packerLoadResults : null;

    if (!selectedPackerEnvelope || selectedPackerEnvelope.length == 1) {
      this.envelopeMissing = true;
      this.plot.data = [];
      this.isLoading.set(false);
      this.needToCalculate = false;
      return;
    } else if (!packerLoads) {
      this.plot.data = [];
      this.needToCalculate = true;
      this.envelopeMissing = false;
      this.isLoading.set(false);
      return;
    } else {
      this.envelopeMissing = false;
      this.needToCalculate = false;
    }

    const packerLoadData = packerLoads.filter(x => !isNaN(x.differentialPressure) && !isNaN(x.tubingToPackerForce)).map(x => {
    const markerSymbols = [
      'circle', 'square', 'diamond', 'cross', 'x', 'triangle-up', 
      'triangle-down', 'triangle-left', 'triangle-right', 'pentagon', 'hexagon'
    ];

    return {
      x: [-x.differentialPressure.toFixed(2)],
      y: [-x.tubingToPackerForce.toFixed(2)],
      mode: 'markers',
      name: x.loadCaseName,
      marker: {
        size: 10,
        symbol: markerSymbols[Math.floor(Math.random() * markerSymbols.length)],
        line: {
          width: 2
        }
      }
    };
    });

    if (selectedPackerEnvelope[0].force !== selectedPackerEnvelope[selectedPackerEnvelope.length - 1].force ||
      selectedPackerEnvelope[0].differentialPressure !== selectedPackerEnvelope[selectedPackerEnvelope.length - 1].differentialPressure) {
      selectedPackerEnvelope.push(selectedPackerEnvelope[0]);
    }

    this.plot.data = [{
      x: selectedPackerEnvelope.map(x => x.differentialPressure),
      y: selectedPackerEnvelope.map(x => x.force),
      mode: 'markers+lines',
      showlegend: false,
      name: 'POE',
      line: {
        color: 'red',
      },
      marker: {
        size: 2
      }
    }];

    this.plot.data.push(...packerLoadData);

    this.isLoading.set(false);
  }

  get isDialogVisible(): boolean {
    return this.needToCalculate || this.packersMissing || this.envelopeMissing;
  }

  ngOnDestroy() {
    this.signalRfunc = null;
    this._subscriptions?.unsubscribe();
    this.packerEnvelopeStore.reset();
  }
}
